// Multiplayer lobby browser. Lists active host advertisements pulled
// from the chat-channel discovery (Pusher fan-out + chat history).
// Wired to the `mp-hosts-changed` window event so the list updates the
// moment a HOST_ADVERTISEMENT or HOST_LEAVING fires anywhere.

import { findActiveHostsInChat } from './chat.js';
import { getUser } from './views.js';
import { getMyPeerId } from '../services/MultiplayerService.js';

let snordeluxeForSession = null;
let hostsChangedHandler = null;
let refreshDebounceTimer = null;
let pollIntervalId = null;

// Poll the chat backend every few seconds while the lobby is open.
// The Pusher fan-out on snordeluxe-server currently doesn't fire (the
// POST /api/chat function hangs on pusher.trigger and 504s), so the
// `mp-hosts-changed` push path can't be relied on. Polling /api/chat/recent
// directly still works because the DB write happens before the hang.
const HOST_POLL_INTERVAL_MS = 4000;

function $(id) { return document.getElementById(id); }

// Persisted host setting: lives-per-player for the next hosted match.
// Stored in localStorage so the host's choice survives reloads. Read by
// becomeHost() in game.js; written here whenever the input changes.
const LIVES_STORAGE_KEY = 'snordeluxe.host.lives';
const LIVES_DEFAULT = 30;
const LIVES_MIN = 1;
const LIVES_MAX = 99;

function readLivesSetting() {
    try {
        const raw = window.localStorage.getItem(LIVES_STORAGE_KEY);
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n >= LIVES_MIN && n <= LIVES_MAX) return n;
    } catch (_) {}
    return LIVES_DEFAULT;
}

export function getHostLivesSetting() {
    return readLivesSetting();
}

function writeLivesSetting(n) {
    try { window.localStorage.setItem(LIVES_STORAGE_KEY, String(n)); } catch (_) {}
}

// Persisted host setting: game-mode toggles for the next hosted match.
// Each is a boolean; the bag is JSON-encoded in one key so we don't grow
// 4+ localStorage entries. Game-mode UI is hidden from non-hosts (Quick
// Match / clients) but everyone shares the same persistence layer.
const MODES_STORAGE_KEY = 'snordeluxe.host.modes';
const MODE_KEYS = ['lifesteal', 'fatMode', 'glassCannon', 'lowGravity'];
function emptyModes() {
    const m = {};
    MODE_KEYS.forEach(k => { m[k] = false; });
    return m;
}

function readModesSetting() {
    try {
        const raw = window.localStorage.getItem(MODES_STORAGE_KEY);
        if (!raw) return emptyModes();
        const parsed = JSON.parse(raw);
        const out = emptyModes();
        MODE_KEYS.forEach(k => { if (parsed && typeof parsed[k] === 'boolean') out[k] = parsed[k]; });
        return out;
    } catch (_) {
        return emptyModes();
    }
}

export function getHostGameModes() {
    return readModesSetting();
}

function writeModesSetting(modes) {
    try { window.localStorage.setItem(MODES_STORAGE_KEY, JSON.stringify(modes)); } catch (_) {}
}

function closeLobby() {
    const modal = $('lobbyBrowserModal');
    if (modal) modal.style.display = 'none';
    if (hostsChangedHandler) {
        window.removeEventListener('mp-hosts-changed', hostsChangedHandler);
        hostsChangedHandler = null;
    }
    if (refreshDebounceTimer) {
        clearTimeout(refreshDebounceTimer);
        refreshDebounceTimer = null;
    }
    if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
    }
}

function statusLabel(host) {
    if (host.state === 'in_progress') return { text: 'In Progress', cls: 'lobby-state-progress' };
    if (host.state === 'full') return { text: 'Full', cls: 'lobby-state-full' };
    return { text: 'Waiting', cls: 'lobby-state-waiting' };
}

async function refreshHostList() {
    const list = $('lobbyHostList');
    const status = $('lobbyBrowserStatus');
    if (!list) return;
    // Filter ONLY by peer ID — same Kinde account opened in two
    // browsers shows up as the same username but with two distinct
    // peer IDs, and we want each one to see the other's host.
    const myPeerId = (() => { try { return getMyPeerId(); } catch (_) { return null; } })();
    const allHosts = await findActiveHostsInChat();
    const hosts = allHosts.filter(h => !myPeerId || h.hostPeerId !== myPeerId);
    console.log(`[Lobby] fetched ${allHosts.length} hosts (showing ${hosts.length} after self-filter)`, allHosts);

    list.innerHTML = '';
    if (!hosts.length) {
        if (status) status.textContent = allHosts.length
            ? 'Only your own match is open — wait for someone to join, or quit and try Quick Match.'
            : 'No active matches — be the first.';
        return;
    }
    if (status) status.textContent = `${hosts.length} open match${hosts.length === 1 ? '' : 'es'}`;

    hosts.forEach(host => {
        const row = document.createElement('div');
        row.className = 'lobby-host-row';
        const count = host.currentPlayerCount || 1;
        const max = host.maxPlayers || 4;
        const label = statusLabel(host);
        const joinable = host.state !== 'in_progress' && host.state !== 'full' && (count < max);
        row.innerHTML = `
            <div class="lobby-host-meta">
                <span class="lobby-host-name">${host.hostUsername}</span>
                <span class="lobby-host-count">${count}/${max}</span>
                <span class="lobby-host-state ${label.cls}">${label.text}</span>
            </div>
            <button class="lobby-join-btn" ${joinable ? '' : 'disabled'}>Join</button>
        `;
        row.querySelector('.lobby-join-btn').addEventListener('click', async () => {
            const { joinHost } = await import('../gameplay/game.js');
            closeLobby();
            joinHost(host.hostPeerId, snordeluxeForSession, host.hostUsername);
        });
        list.appendChild(row);
    });
}

function scheduleRefresh() {
    if (refreshDebounceTimer) clearTimeout(refreshDebounceTimer);
    refreshDebounceTimer = setTimeout(() => {
        refreshDebounceTimer = null;
        refreshHostList();
    }, 150);
}

export function showLobbyBrowser(snordeluxeName) {
    snordeluxeForSession = snordeluxeName;
    const modal = $('lobbyBrowserModal');
    if (!modal) return;
    modal.style.display = 'flex';

    const closeBtn = $('lobbyBrowserClose');
    if (closeBtn) closeBtn.onclick = closeLobby;
    modal.onclick = (e) => { if (e.target === modal) closeLobby(); };

    $('lobbyHostList').innerHTML = '';
    $('lobbyBrowserStatus').textContent = 'Loading open matches…';

    $('lobbyQuickMatch').onclick = async () => {
        const { joinHost } = await import('../gameplay/game.js');
        const myPeerId = (() => { try { return getMyPeerId(); } catch (_) { return null; } })();
        const hosts = (await findActiveHostsInChat()).filter(h =>
            (!myPeerId || h.hostPeerId !== myPeerId) &&
            h.state !== 'in_progress' && h.state !== 'full'
        );
        if (hosts.length) {
            const target = hosts[0];
            closeLobby();
            joinHost(target.hostPeerId, snordeluxeForSession, target.hostUsername);
        } else {
            // No open match → fall through to host-config modal so the
            // user still gets the lives + game-mode question before going live.
            openHostConfigModal();
        }
    };
    $('lobbyHostNew').onclick = () => {
        openHostConfigModal();
    };
    $('lobbySingle').onclick = async () => {
        const { playSingle } = await import('../gameplay/game.js');
        closeLobby();
        playSingle(snordeluxeForSession);
    };

    if (!hostsChangedHandler) {
        hostsChangedHandler = () => scheduleRefresh();
        window.addEventListener('mp-hosts-changed', hostsChangedHandler);
    }
    refreshHostList();

    if (pollIntervalId) clearInterval(pollIntervalId);
    pollIntervalId = setInterval(() => scheduleRefresh(), HOST_POLL_INTERVAL_MS);
}

// Host Match Setup modal. Opened from the lobby's "Host New Match"
// button. Pre-fills inputs from the persisted settings, lets the host
// confirm/change lives + game modes, then commits + closes the lobby +
// fires becomeHost. Cancel just hides the modal and returns to the
// lobby browser.
function openHostConfigModal() {
    const modal = document.getElementById('hostConfigModal');
    if (!modal) {
        // Fallback: dialog missing in DOM — go straight to becomeHost
        // with the saved settings rather than blocking the host.
        import('../gameplay/game.js').then(({ becomeHost }) => {
            closeLobby();
            becomeHost(snordeluxeForSession);
        });
        return;
    }

    const livesInput = document.getElementById('hostConfigLives');
    const modeInputs = {
        lifesteal: document.getElementById('hostConfigLifesteal'),
        fatMode: document.getElementById('hostConfigFat'),
        glassCannon: document.getElementById('hostConfigGlassCannon'),
        lowGravity: document.getElementById('hostConfigLowGravity'),
    };

    // Seed inputs from persisted state so the modal acts as a confirm
    // step on the existing defaults.
    if (livesInput) livesInput.value = String(readLivesSetting());
    const modes = readModesSetting();
    MODE_KEYS.forEach(k => {
        const inp = modeInputs[k];
        if (inp) inp.checked = !!modes[k];
    });

    modal.style.display = 'flex';

    const close = () => {
        modal.style.display = 'none';
        modal.onclick = null;
        const closeBtn = document.getElementById('hostConfigClose');
        const cancelBtn = document.getElementById('hostConfigCancel');
        const startBtn = document.getElementById('hostConfigStart');
        if (closeBtn) closeBtn.onclick = null;
        if (cancelBtn) cancelBtn.onclick = null;
        if (startBtn) startBtn.onclick = null;
    };
    modal.onclick = (e) => { if (e.target === modal) close(); };
    const closeBtn = document.getElementById('hostConfigClose');
    const cancelBtn = document.getElementById('hostConfigCancel');
    const startBtn = document.getElementById('hostConfigStart');
    if (closeBtn) closeBtn.onclick = close;
    if (cancelBtn) cancelBtn.onclick = close;
    if (startBtn) startBtn.onclick = async () => {
        // Commit lives (clamped) + modes to localStorage so becomeHost
        // reads the just-confirmed values via getHostLivesSetting /
        // getHostGameModes.
        if (livesInput) {
            const raw = parseInt(livesInput.value, 10);
            const n = Number.isFinite(raw)
                ? Math.max(LIVES_MIN, Math.min(LIVES_MAX, raw))
                : LIVES_DEFAULT;
            writeLivesSetting(n);
        }
        const next = readModesSetting();
        MODE_KEYS.forEach(k => {
            const inp = modeInputs[k];
            if (inp) next[k] = !!inp.checked;
        });
        writeModesSetting(next);

        const { becomeHost } = await import('../gameplay/game.js');
        close();
        closeLobby();
        becomeHost(snordeluxeForSession);
    };
}

export { closeLobby };
